import React, { useEffect, useRef, useState } from 'react';

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import styled from 'styled-components';

import ThreeCardCard from './ThreeCardCard';

gsap.registerPlugin(ScrollTrigger);

export type CardData = {
  heading: string;
  copy: React.ReactNode;
  image: React.ReactNode;
  button?: React.ReactNode | null;
};

export interface ThreeCardComponentProps extends React.HTMLAttributes<HTMLElement> {
  cards: CardData[];
}

const Wrapper = styled.section`
  display: flex;
  flex-direction: column;
  position: relative;
  margin-bottom: 50px;

  .card-wrapper {
    position: sticky;
    top: 0;
    z-index: 10;

    &:last-child {
      position: relative;
      top: auto;
    }
  }

  .stacked-1 {
    z-index: 8;
  }
  .stacked-2 {
    z-index: 9;
  }
  .bottom-cta-section {
    background-color: transparent;
    padding: 40px 0 0 !important;
    .content {
      > div {
        justify-content: space-between;
        .--left {
          max-width: 372px;
        }
      }
    }
  }

  @media (max-width: 768px) {
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    gap: 12px;
    padding: 0 25px;
    -webkit-overflow-scrolling: touch;
    width: 100%;
    position: relative;
    align-items: stretch;

    .bottom-cta-section {
      padding: 0 !important;
      height: 100%;
      .content {
        margin: 0;
        padding: 0 !important;
        height: 100%;
        > div {
          flex-direction: column;
          border-radius: 20px;
          gap: 40px;
          height: 100%;
          .--left {
            padding: 40px 20px 0;
            .--copy {
              margin-bottom: 0px;
            }
          }
        }
      }
    }

    &::-webkit-scrollbar {
      display: none;
    }
    scrollbar-width: none;

    .card-wrapper {
      flex: 0 0 75vw;
      position: relative;
      top: auto;
      margin: 0;
      scroll-snap-align: start;
      display: flex;
      height: auto;
    }
  }
`;

const ThreeCardComponent: React.FC<ThreeCardComponentProps> = ({ cards, ...rest }) => {
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);

  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 769
  );
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 769);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    ScrollTrigger.getAll().forEach((t) => t.kill());

    if (!isDesktop) return;

    if (!sectionRef.current || cardRefs.current.length === 0) return;

    const spacer = 20;
    const minScale = 0.8;
    const distributor = gsap.utils.distribute({ base: minScale, amount: 0.2 });

    cardRefs.current.forEach((card, index) => {
      if (!card) return;

      const bottomCta = card.querySelector('.bottom-cta-section');
      if (!bottomCta) return;
      if (index === cards.length - 1) return;

      const scaleVal = distributor(index, cardRefs.current[index], cardRefs.current);

      gsap.to(bottomCta, {
        scrollTrigger: {
          trigger: card,
          start: 'top top+=100',
          end: 'top+=100',
          scrub: 1,
          invalidateOnRefresh: true,
        },
        ease: 'power2.out',
        scale: scaleVal,
      });

      const finalCardIndex = cards.length - 1;
      const endPosition = `top-=${finalCardIndex * spacer} top+=165`;
      const startOffset = index === 1 ? 120 : 80;

      ScrollTrigger.create({
        trigger: card,
        start: `top-=${index * spacer} top+=${startOffset}`,
        endTrigger: cardRefs.current[finalCardIndex],
        end: endPosition,
        pin: true,
        pinSpacing: false,
        id: `pin-${index}`,
        invalidateOnRefresh: true,
      });
    });

    /* ensure ScrollTrigger recalculates */
    ScrollTrigger.refresh();
  }, [cards.length, isDesktop]); // 👈 breakpoint in deps

  return (
    <Wrapper {...rest} ref={sectionRef}>
      {cards.map(({ heading, copy, image, button = null }, idx) => (
        <div
          key={idx}
          className={`card-wrapper stacked-${idx + 1}`}
          ref={(el) => (cardRefs.current[idx] = el)}
        >
          <ThreeCardCard heading={heading} copy={copy} button={button} image={image} />
        </div>
      ))}
    </Wrapper>
  );
};

export default ThreeCardComponent;
